import React, { useEffect, useState } from 'react'
import AlertUI from '../../generic/AlertUI'
import { format as formatDateFns } from 'date-fns'
import { apiPost, apiGet } from '../../generic/Api_Functions'
import { DateTimePicker } from '@mui/x-date-pickers'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import {
  Box,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stack,
  Button,
  Paper
} from '@mui/material'
import { useUser } from '../../generic/hooks/useUser'
import useFormUtils from './useFormUtils'

const formatToRequestDate = (value) => {
  if (!value) return ''

  const date =
    typeof value?.toDate === 'function'
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value)

  if (!date || Number.isNaN(date.getTime())) {
    return ''
  }

  return formatDateFns(date, 'yyyy-MM-dd')
}

export default function ServiceRequestForm ({ orderRow, onClose }) {
  const { format: dateFormats } = useDateFormatter()
  const todayFormatted = formatToRequestDate(new Date())
  const { t_ } = useSmartTranslation()
  const [errors, setErrors] = useState({})
  const [AlertElement, showAlert] = AlertUI()
  const [nodes, setNodes] = useState([])
  const [currentNodeName, setCurrentNodeName] = useState('')
  const { user } = useUser()
  const [submitting, setSubmitting] = useState(false)
  const [orderPendingStatus, setOrderPendingStatus] = useState(null)
  const [client, setClient] = useState(null)
  const [parentOrderLine, setParentOrderLine] = useState({})
  const { isEjercito } = useFormUtils()
  useEffect(() => {
    const checkIfIsArmyProfile = () => {
      if (isEjercito) {
        setForm(prev => ({
          ...prev,
          solicitante: {
            nombre: user.name + ' ' + user.surname,
            email: user.email || ''
          }
        }))
      }
    }

    checkIfIsArmyProfile()
  }, [user, isEjercito])

  useEffect(() => {
    const getEjercitoClient = async () => {
      const result = await apiGet('client/byCode/EJERCITO')
      setClient(result)
    }
    isEjercito && getEjercitoClient()
  }, [isEjercito])

  useEffect(() => {
    const loadNodes = async () => {
      const response = await apiGet('cluster/getNodes')
      setNodes(response)
    }
    loadNodes()
  }, [])

  useEffect(() => {
    const loadCurrentNode = async () => {
      const response = await apiGet('cluster/getNodeName')
      setCurrentNodeName(response?.message || '')
    }
    loadCurrentNode()
  }, [])

  useEffect(() => {
    const fetchOrderPendingStatus = async () => {
      const orderPendingStatus = await apiGet('simpleOrderStatus/byCode/PENDING')
      setOrderPendingStatus(orderPendingStatus)
    }
    fetchOrderPendingStatus()
  }, [])

  useEffect(() => {
    const fetchParentOrderLine = async () => {
      if (!orderRow?.id) return
      const lineOrders = await apiGet(`order/${orderRow.id}/lineOrders/notPaginated`)
      const line = lineOrders?.[0]
      if (line) {
        setParentOrderLine(line)
        setForm(prev => ({
          ...prev,
          quantity: String(line.quantity ?? '')
        }))
      }
    }
    fetchParentOrderLine()
  }, [orderRow])

  const [form, setForm] = useState({
    type: 'SERVICE_REQUEST',
    codigoSolicitud: '',
    codigoProyecto: '',
    nombreProyecto: '',
    elaboradoPor: user.name + ' ' + user.surname,
    centroDestino: '',
    centroLetra: '',
    fechaSolicitud: todayFormatted,
    fechaObjetivo: null,
    nivelUrgencia: '',
    tipoSolicitud: 'Servicio',
    quantity: '',
    descripcion: '',
    rugosimetro: false,
    medicion3D: false,
    instrumentosManual: false,
    escaneado3D: false,
    disenoCAD: false,
    FEM: false,
    CFD: false,
    pt: false,
    mt: false,
    ut: false,
    rt: false,
    ct: false,
    dureza: false,
    impactoCharpy: false,
    traccion: false,
    traccionAltaTemp: false,
    flexionPolimericos: false,
    fatiga: false,
    fatigaAltaTemp: false,
    DSC: false,
    dilatometria: false,
    ICP: false,
    LECO: false,
    EDS: false,
    PMI: false,
    SEM: false,
    optica: false,
    metalografia: false,
    nieblaSalina: false,
    potenciostato: false,
    ttTermicos: false,
    ttSuperficiales: false,
    digiOtroString: '',
    digiOtroBool: false,
    calcOtroString: '',
    calcOtroBool: false,
    endOtroString: '',
    endOtroBool: false,
    fisicoOtroString: '',
    fisicoOtroBool: false,
    quimicoOtroString: '',
    quimicoOtroBool: false,
    otrosString: '',
    otrosBool: false
  })

  const handleEnableField = (name) => (event) => {
    const checked = event.target.checked
    setForm(prev => ({
      ...prev,
      [name + 'Bool']: checked,
      [name + 'String']: checked ? prev[name + 'String'] : '' // si se desactiva, vaciamos el texto
    }))
  }

  const generateCode = async (fecha, centroLetra, centro) => {
    const yyyy = new Date().getFullYear().toString()
    const sufijo = 'SSE'

    // Obtener el último código del backend
    let lastCode
    try {
      lastCode = await getLastOrder(yyyy, centroLetra, sufijo, centro)
    } catch (error) {
      setSubmitting(false)
      showAlert({ severity: 'error', title: t_(error.message) })
      return
    }

    if (lastCode === null) {
      const nextNumber = '1'.padStart(9, '0')
      return `${yyyy}${nextNumber}${centroLetra}${sufijo}`
    }

    const prefixLength = 4
    const numericPart = lastCode.substring(prefixLength, prefixLength + 9)
    const nextNumber = (parseInt(numericPart, 10) + 1)
      .toString()
      .padStart(9, '0')

    return `${yyyy}${nextNumber}${centroLetra}${sufijo}`
  }

  const getLastOrder = async (year, middlepart, sufix, centro) => {
    const response = await apiGet(`order/lastByYearAndMiddleByNode/${year}/${middlepart}/${sufix}?node=${centro}`
    ).catch((error) => {
      console.error('Error fetching last order:', error)
      throw error
    })
    return response?.message ?? null
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const validateForm = () => {
    const newErrors = {}
    if (!form.elaboradoPor) newErrors.elaboradoPor = true
    if (!form.centroDestino) newErrors.centroDestino = true
    if (!form.fechaObjetivo) newErrors.fechaObjetivo = true
    if (!form.nivelUrgencia) newErrors.nivelUrgencia = true
    if (!form.tipoSolicitud) newErrors.tipoSolicitud = true

    const q = form.quantity
    if (q === '' || q === null || q === undefined) {
      newErrors.quantity = true
    } else {
      const n = Number(q)
      if (Number.isNaN(n) || !Number.isInteger(n) || n < 0) {
        newErrors.quantity = true
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      showAlert({ severity: 'error', title: t_('Please fill in all required fields') })
      return
    }

    setSubmitting(true)

    // Guardar la hora del submit junto con la fecha
    const selectedDate = new Date(form.fechaObjetivo)
    const codigoGenerado = await generateCode(form.fechaObjetivo, form.centroLetra, form.centroDestino)
    if (!codigoGenerado) {
      return
    }
    form.codigoSolicitud = codigoGenerado
    try {
      const currentDate = new Date()
      const payload = {
        id: null,
        code: codigoGenerado,
        date: currentDate,
        targetDate: selectedDate || null,
        userSetTargetDate: orderRow.userSetTargetDate,
        client: client != null ? client : orderRow.client,
        lineOrderQuantity: orderRow.lineOrderQuantity,
        forecast: false,
        expirationDate: orderRow.expirationDate,
        status: orderPendingStatus,

        extended_column1: form.centroDestino, // Centro
        extended_column2: form.nivelUrgencia, // Prioridad
        extended_column3: orderRow?.code ?? null, // Pedido relacionado (Orden padre)
        extended_column4: !isEjercito ? form.elaboradoPor : '', // Responsable pedido
        extended_column5: orderRow?.extended_column5, // Código proyecto
        extended_column6: orderRow?.extended_column6, // Nombre proyecto
        extended_column10: parentOrderLine?.partReference.code,

        extended_column7: null, // Solicitante POC
        extended_column8: null, // Número plano
        extended_column9: null,
        extended_column11: null,
        extended_column12: null,
        // Origin: name of the node that issues the request (own node)
        extended_column13: currentNodeName,
        // Destination: name of the node the request is sent to (selected center)
        extended_column14: form.centroDestino
      }
      // Order creation. When the target center is the current node, create it locally without
      // syncing to the other cluster nodes (a request to itself does not need cross-node sync).
      const isSameNode = !!currentNodeName &&
        form.centroDestino.trim().toLowerCase() === currentNodeName.trim().toLowerCase()
      const ordersEndpoint = isSameNode ? 'orders' : 'orders?syncWithNode=' + form.centroDestino
      const createdOrder = await apiPost(ordersEndpoint, payload)

      const newLineOrder = {
        id: null,
        lineNumber: parentOrderLine?.lineNumber ?? '1',
        partReference: parentOrderLine?.partReference,
        quantity: Number(form.quantity),
        targetDate: selectedDate ? new Date(selectedDate).toISOString() : null,
        comment: form.descripcion,
        active: true,
        fabricationOrderQuantity: parentOrderLine?.fabricationOrderQuantity ?? 0,
        forecast: false,
        expirationDate: null
      }

      // LineOrder creation. Pass the selected destination node as targetNodeName (the backend resolves
      // the name to the node); omit it when no node is chosen so the backend keeps its current behavior.
      const lineOrderEndpoint = form.centroDestino
        ? `order/${createdOrder.id}/lineOrder?targetNodeName=${encodeURIComponent(form.centroDestino)}`
        : `order/${createdOrder.id}/lineOrder`
      await apiPost(lineOrderEndpoint, newLineOrder)

      form.createdOrderId = createdOrder.id
      form.fechaObjetivo = form.fechaObjetivo.toLocaleDateString()
      form.fechaSolicitud = currentDate.toLocaleDateString()
      form.parentOrderId = orderRow?.id
      await apiPost('report/serviceRequest', form)

      onClose()
    } catch (error) {
      setSubmitting(false)
      showAlert({ severity: 'error', title: t_(error.message) })
      console.error('Error enviando solicitud:', error)
    }
  }
  const textFieldStyles = {
    '& .MuiInputBase-input': { color: '#e3e3e3' },
    '& .MuiInputLabel-root': {
      color: '#e3e3e3',
      '&.Mui-error': { color: '#F68D76' }
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: '#AAAAAA' },
      '&:hover fieldset': { borderColor: '#7FE1A7' },
      '&.Mui-error fieldset': { borderColor: '#F68D76' }
    }
  }

  const selectStyles = {
    color: '#e3e3e3',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#AAAAAA' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7FE1A7' },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#F68D76' }
  }
  const SectionTitle = ({ children }) => (
    <Grid item xs={12}>
      <Typography
        variant='h6'
        sx={{
          color: '#7FE1A7',
          borderBottom: '2px solid #444',
          paddingBottom: 1,
          marginBottom: 2
        }}
      >
        {children}
      </Typography>
    </Grid>
  )

  return (
    <>
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 9999
      }}
      >
        {AlertElement}
      </div>
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          padding: 3,
          backgroundColor: '#1b1b1b',
          color: '#e3e3e3',
          borderRadius: 2,
          maxWidth: 1000,
          margin: '0 auto'
        }}
      >
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          {/* Encabezado */}
          <SectionTitle> {t_('APPLICATION DETAILS')}</SectionTitle>

          <Grid container spacing={2}>
            {/* Nombre y apellidos */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={`${t_('Prepared by')} *`}
                name='elaboradoPor'
                placeholder={t_('Name and surname of the person responsible')}
                sx={textFieldStyles}
                value={form.elaboradoPor}
                onChange={handleChange}
                error={!!errors.elaboradoPor}
              />

            </Grid>
            {/* Centro */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.centroDestino}>
                <InputLabel>{`${t_('Center')} *`}</InputLabel>
                <Select
                  name='centro'
                  label={`${t_('Center')} *`}
                  align='left'
                  value={form.centroDestino}
                  sx={selectStyles}
                  onChange={(e) => {
                    const letra = e.target.value
                    setForm(prev => ({
                      ...prev,
                      centroDestino: letra,
                      centroLetra: letra[0]
                    }))
                  }}
                >
                  <MenuItem value=''>Seleccionar Centro</MenuItem>
                  {nodes.map(m => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Fecha solicitud */}
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label={t_('Target date')}
                value={form.fechaObjetivo ? new Date(form.fechaObjetivo) : null}
                inputFormat={dateFormats.date}
                onChange={(newValue) => {
                  setForm((prevForm) => ({
                    ...prevForm,
                    fechaObjetivo: newValue
                  }))
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.fechaObjetivo
                  }
                }}
              />
            </Grid>
            {/* Nivel de urgencia */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.nivelUrgencia}>
                <InputLabel>{`${t_('Urgency level')} *`}</InputLabel>
                <Select
                  name='nivelUrgencia'
                  label={`${t_('Urgency level')} *`}
                  value={form.nivelUrgencia}
                  onChange={handleChange}
                  align='left'
                  error={!!errors.nivelUrgencia}
                  sx={selectStyles}
                  inputProps={{
                    sx: errors.nivelUrgencia ? { borderColor: 'red' } : {}
                  }}
                >
                  <MenuItem value=''>{t_('Select')}</MenuItem>
                  <MenuItem value='BAJA'>{t_('Low')}</MenuItem>
                  <MenuItem value='MEDIA'>{t_('Middle')}</MenuItem>
                  <MenuItem value='ALTA'>{t_('High')}</MenuItem>
                  <MenuItem value='CRITICA'>{t_('Critical')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Tipo de solicitud */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.tipoSolicitud}>
                <InputLabel>{`${t_('Request type')} *`}</InputLabel>
                <Select
                  name='tipoSolicitud'
                  label={`${t_('Request type')} *`}
                  value={form.tipoSolicitud}
                  onChange={handleChange}
                  sx={selectStyles}
                  align='left'
                  error={!!errors.tipoSolicitud}
                  inputProps={{
                    sx: errors.tipoSolicitud ? { borderColor: 'red' } : {}
                  }}
                >
                  <MenuItem value='Servicio'>{t_('Service')}</MenuItem>
                  <MenuItem value='Fabricación'>{t_('Fabrication')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Cantidad — CA1: exactamente un campo, a la derecha de "Request type" */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label={`${t_('Quantity')} *`}
                name='quantity'
                value={form.quantity}
                onChange={handleChange}
                sx={textFieldStyles}
                error={!!errors.quantity}
              />
            </Grid>
          </Grid>

          {/* DESCRIPCIÓN */}
          <br />
          <TextField
            sx={textFieldStyles}
            fullWidth
            multiline
            rows={4}
            label={t_('Description')}
            name='descripcion'
            value={form.descripcion}
            onChange={handleChange}
          />
        </Paper>

        {/* DIGITALIZACIÓN Y METROLOGÍA */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <SectionTitle> {t_('DIGITALIZATION AND METROLOGY')}</SectionTitle>

          {/* Rugosímetro de contacto */}
          <Stack spacing={1}>
            <FormControlLabel
              control={<Checkbox checked={form.rugosimetro} name='rugosimetro' onChange={handleChange} />}
              label={t_('Contact roughness tester')}
            />
            {/* Sistema de medición tridimensional */}
            <FormControlLabel
              control={<Checkbox checked={form.medicion3D} name='medicion3D' onChange={handleChange} />}
              label={t_('Three-dimensional measurement system')}
            />
            {/* Instrumentos de medición manual */}
            <FormControlLabel
              control={<Checkbox checked={form.instrumentosManual} name='instrumentosManual' onChange={handleChange} />}
              label={t_('Manual measuring instruments')}
            />
            {/* Sistema de escaneado 3D */}
            <FormControlLabel
              control={<Checkbox checked={form.escaneado3D} name='escaneado3D' onChange={handleChange} />}
              label={t_('3D scanning system')}
            />
            {/* Diseño CAD */}
            <FormControlLabel
              control={<Checkbox checked={form.disenoCAD} name='disenoCAD' onChange={handleChange} />}
              label={t_('CAD design')}
            />
            {/* Otro */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.digiOtroBool}
                  onChange={handleEnableField('digiOtro')}
                />
            }
              label={t_('Other')}
            />

            <TextField
              fullWidth
              sx={textFieldStyles}
              inputProps={{ maxLength: 200 }}
              disabled={!form.digiOtroBool}
              name='digiOtroString'
              value={form.digiOtroString}
              onChange={handleChange}
            />

          </Stack>
        </Paper>
        {/* CÁLCULO */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <SectionTitle> {t_('CALCULATION')}</SectionTitle>

          <Stack spacing={1}>
            <FormControlLabel
              control={<Checkbox checked={form.FEM} name='FEM' onChange={handleChange} />}
              label='FEM'
            />
            <FormControlLabel
              control={<Checkbox checked={form.CFD} name='CFD' onChange={handleChange} />}
              label='CFD'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.calcOtroBool}
                  onChange={handleEnableField('calcOtro')}
                />
              }
              label={t_('Other')}
            />

            <TextField
              fullWidth
              sx={textFieldStyles}
              disabled={!form.calcOtroBool}
              name='calcOtroString'
              inputProps={{ maxLength: 200 }}
              value={form.calcOtroString}
              onChange={handleChange}
            />
          </Stack>
        </Paper>
        {/* ENSAYOS NO DESTRUCTIVOS (END) */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <SectionTitle> {t_('NON-DESTRUCTIVE TESTING (NDT)')}</SectionTitle>

          <Stack spacing={1}>
            {/* Líquidos penetrantes (PT) */}
            <FormControlLabel
              control={<Checkbox checked={form.pt} name='pt' onChange={handleChange} />} label={`${t_('Penetrating liquids')} (PT)`}
            />
            {/* Partículas magnéticas (MT) */}
            <FormControlLabel
              control={<Checkbox checked={form.mt} name='mt' onChange={handleChange} />} label={`${t_('Magnetic particles')} (MT)`}
            />
            {/* Medición de espesores por ultrasonidos (UT) */}
            <FormControlLabel
              control={<Checkbox checked={form.ut} name='ut' onChange={handleChange} />} label={`${t_('Ultrasonic thickness measurement')} (UT)`}
            />
            {/* Radiografía (RT) */}
            <FormControlLabel
              control={<Checkbox checked={form.rt} name='rt' onChange={handleChange} />} label={`${t_('Radiography')} (RT)`}
            />
            {/* Tomografía computarizada (CT) */}
            <FormControlLabel
              control={<Checkbox checked={form.ct} name='ct' onChange={handleChange} />} label={`${t_('Computed tomography')} (CT)`}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.endOtroBool}
                  onChange={handleEnableField('endOtro')}
                />
              }
              label={t_('Other')}
            />

            <TextField
              fullWidth
              sx={textFieldStyles}
              disabled={!form.endOtroBool}
              name='endOtroString'
              inputProps={{ maxLength: 200 }}
              value={form.endOtroString}
              onChange={handleChange}
            />
          </Stack>
        </Paper>
        {/* ENSAYOS FÍSICOS */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <SectionTitle> {t_('PHYSICAL TESTS')}</SectionTitle>
          {/* Dureza */}
          <Stack spacing={1}>
            <FormControlLabel
              key='Hardness'
              control={<Checkbox checked={form.dureza} name='dureza' onChange={handleChange} />}
              label={t_('Hardness')}
            />
            {/* Impacto Charpy */}
            <FormControlLabel
              key='Charpy impact'
              control={<Checkbox checked={form.impactoCharpy} name='impactoCharpy' onChange={handleChange} />}
              label={t_('Charpy impact')}
            />
            {/* Ensayo de tracción */}
            <FormControlLabel
              key='Tensile test'
              control={<Checkbox checked={form.traccion} name='traccion' onChange={handleChange} />}
              label={t_('Tensile test')}
            />
            {/* Ensayo de tracción (alta temperatura) */}
            <FormControlLabel
              key='High temperature tensile test'
              control={<Checkbox checked={form.traccionAltaTemp} name='traccionAltaTemp' onChange={handleChange} />}
              label={t_('High temperature tensile test')}
            />
            {/* Ensayo de flexión (materiales poliméricos) */}
            <FormControlLabel
              key='Flexural test (polymeric materials)'
              control={<Checkbox checked={form.flexionPolimericos} name='flexionPolimericos' onChange={handleChange} />}
              label={t_('Flexural test (polymeric materials)')}
            />
            {/* Ensayo de fatiga */}
            <FormControlLabel
              key='Fatigue test'
              control={<Checkbox checked={form.fatiga} name='fatiga' onChange={handleChange} />}
              label={t_('Fatigue test')}
            />
            {/* Ensayo de fatiga (alta temperatura) */}
            <FormControlLabel
              key='Fatigue test (High temperature)'
              control={<Checkbox checked={form.fatigaAltaTemp} name='fatigaAltaTemp' onChange={handleChange} />}
              label={t_('Fatigue test (High temperature)')}
            />
            {/* Calorimetría Diferencial de Barrido (DSC) */}
            <FormControlLabel
              key='Differential Scanning Calorimetry (DSC)'
              control={<Checkbox checked={form.DSC} name='DSC' onChange={handleChange} />}
              label={t_('Differential Scanning Calorimetry (DSC)')}
            />
            {/* Análisis Termo Mecánico / Dilatometría */}
            <FormControlLabel
              key='Thermo Mechanical Analysis / Dilatometry'
              control={<Checkbox checked={form.dilatometria} name='dilatometria' onChange={handleChange} />}
              label={t_('Thermo Mechanical Analysis / Dilatometry')}
            />
            {/* Otros */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.fisicoOtroBool}
                  onChange={handleEnableField('fisicoOtro')}
                />
              }
              label={t_('Other')}
            />

            <TextField
              fullWidth
              sx={textFieldStyles}
              disabled={!form.fisicoOtroBool}
              name='fisicoOtroString'
              inputProps={{ maxLength: 200 }}
              value={form.fisicoOtroString}
              onChange={handleChange}
            />
          </Stack>
        </Paper>
        {/* ANÁLISIS QUÍMICO */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <SectionTitle> {t_('CHEMICAL ANALYSIS')}</SectionTitle>

          <Stack spacing={1}>
            <FormControlLabel control={<Checkbox checked={form.ICP} name='ICP' onChange={handleChange} />} label='ICP' />
            <FormControlLabel control={<Checkbox checked={form.LECO} name='LECO' onChange={handleChange} />} label='LECO NHO' />
            <FormControlLabel control={<Checkbox checked={form.EDS} name='EDS' onChange={handleChange} />} label='EDS' />
            <FormControlLabel control={<Checkbox checked={form.PMI} name='PMI' onChange={handleChange} />} label='PMI' />

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.quimicoOtroBool}
                  onChange={handleEnableField('quimicoOtro')}
                />
              }
              label={t_('Other')}
            />

            <TextField
              fullWidth
              sx={textFieldStyles}
              disabled={!form.quimicoOtroBool}
              name='quimicoOtroString'
              inputProps={{ maxLength: 200 }}
              value={form.quimicoOtroString}
              onChange={handleChange}
            />

          </Stack>
        </Paper>
        {/* MICROSCOPÍA */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <SectionTitle> {t_('MICROSCOPY')}</SectionTitle>
          <Stack spacing={1}>
            <FormControlLabel control={<Checkbox checked={form.SEM} name='SEM' onChange={handleChange} />} label='SEM' />
            <FormControlLabel control={<Checkbox checked={form.optica} name='optica' onChange={handleChange} />} label='Óptica' />
          </Stack>
        </Paper>
        {/* METALOGRAFÍA */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <SectionTitle> {t_('METALLOGRAPHY')}</SectionTitle>
          <Stack spacing={1}>
            <FormControlLabel control={<Checkbox checked={form.metalografia} name='metalografia' onChange={handleChange} />} label={t_('Metallography')} />
          </Stack>
        </Paper>
        {/* CORROSIÓN */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <SectionTitle> {t_('CORROSION')}</SectionTitle>
          <Stack spacing={1}>
            {/* Prueba de niebla salina */}
            <FormControlLabel
              key='Salt spray test'
              control={<Checkbox checked={form.nieblaSalina} name='nieblaSalina' onChange={handleChange} />}
              label={t_('Salt spray test')}
            />
            {/* Medición electroquímica por potenciostato/galvanostato */}
            <FormControlLabel
              key='Electrochemical measurement by potentiostat/galvanostat'
              control={<Checkbox checked={form.potenciostato} name='potenciostato' onChange={handleChange} />}
              label={t_('Electrochemical measurement by potentiostat/galvanostat')}
            />
          </Stack>
        </Paper>
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <SectionTitle> {t_('HEAT TREATMENTS')}</SectionTitle>
          {/* TRATAMIENTOS TÉRMICOS */}
          <Stack spacing={1}>
            <FormControlLabel
              key='Heat treatments'
              control={<Checkbox checked={form.ttTermicos} name='ttTermicos' onChange={handleChange} />}
              label={t_('Heat treatments')}
            />
          </Stack>
        </Paper>
        {/* TRATAMIENTOS SUPERFICIALES */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <SectionTitle> {t_('SURFACE TREATMENTS')}</SectionTitle>
          <Stack spacing={1}>
            <FormControlLabel
              key='Surface treatments'
              control={<Checkbox checked={form.ttSuperficiales} name='ttSuperficiales' onChange={handleChange} />}
              label={t_('Surface treatments')}
            />
          </Stack>
        </Paper>
        {/* OTROS */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <SectionTitle> {t_('OTHERS')}</SectionTitle>
          <Box display='flex' justifyContent='flex-start'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.otrosBool}
                  onChange={handleEnableField('otros')}
                />
              }
              label={t_('Other')}
            />
          </Box>

          <TextField
            fullWidth
            sx={textFieldStyles}
            disabled={!form.otrosBool}
            name='otrosString'
            inputProps={{ maxLength: 200 }}
            value={form.otrosString}
            onChange={handleChange}
          />
        </Paper>
        {/* BOTÓN ENVIAR */}
        <Button
          type='submit'
          variant='contained'
          disabled={submitting}
          sx={{
            backgroundColor: '#7FE1A7',
            color: '#000',
            fontWeight: 'bold',
            padding: '12px 24px',
            '&:hover': {
              backgroundColor: '#6bd095'
            }
          }}
        >
          {t_('Submit request')}
        </Button>
      </Box>
    </>
  )
}
